import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import {
  Box,
  Typography,
  CardContent,
  CardActions,
  CircularProgress,
} from '@mui/material'
import {
  Wrapper,
  DeleteIconStyle,
  GoToIcon,
  ViewFont,
  Content,
  Title,
  Bold,
  Detail,
  DetailIcon,
  DetailSnapshot,
  SecondaryText,
  Footer,
  FooterDetail,
  FooterDetailIcon,
  FooterText,
  Timestamp,
  PublicPrivateIconContainer,
} from './ProjectCard.styles'
import { ProjectCardProps } from './ProjectCard.types'
import SnapshotIcon from 'Assets/icons/snapshot.svg'
import WorkcentreIcon from 'Assets/icons/workcentre.svg'
import ScenarioIcon from 'Assets/icons/scenario.svg'
import UserIcon from 'Assets/icons/user.svg'
import DateIcon from 'Assets/icons/date.svg'
import TimeIcon from 'Assets/icons/time.svg'
import {
  ChevronRight,
  LockOutlined,
  LockOpenOutlined,
} from '@mui/icons-material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import { PROJECT_VISIBLITY } from 'Common/Constants/Constants'
import { Button } from 'Common/Supplyvue-ui'

const COMPLETED = 'COMPLETED'

export const ProjectCardView = ({
  projectId,
  projectName,
  snapshotName,
  snapshotLength,
  snapshotEndDate,
  workcentres,
  scenarios,
  createdAt,
  createdBy,
  handleClick,
  handleDelete,
  userEmail,
  projectOwnerEmail,
  projectAccessType,
  handlePlanAccessChangeClick,
  isProjectAccessUpdating = false,
  snapshotStatus = COMPLETED,
  isDeleting,
}: ProjectCardProps) => {
  const [projectCardAccessUpdating, setProjectCardAccessUpdating] =
    useState(false)

  useEffect(() => {
    if (!isProjectAccessUpdating) setProjectCardAccessUpdating(false)
  }, [isProjectAccessUpdating])

  return (
    <Wrapper>
      <CardContent component={Content}>
        <Box display="flex" justifyContent="space-between">
          <Typography component={Title} variant="h3" color="textSecondary">
            {projectName}
          </Typography>
          <Button
            disableRipple={true}
            onClick={() => handleDelete(projectId, projectName)}
            startIcon={
              isDeleting ? (
                <CircularProgress size={14} />
              ) : (
                <DeleteOutlineIcon color="error" />
              )
            }
            sx={(theme) => {
              return {
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                alignItems: 'center',
                minWidth: 0,
                padding: 0,
                '&.MuiButton-text:hover': {
                  backgroundColor: 'transparent',
                },
                backgroundColor: 'transparent',
              }
            }}
          />
        </Box>
        {!!snapshotName && (
          <Detail>
            <DetailIcon>
              <SnapshotIcon />
            </DetailIcon>
            <DetailSnapshot>
              <Typography component="span" variant="body1">
                <Bold>{snapshotName}</Bold>
                {snapshotStatus !== COMPLETED && (
                  <CircularProgress sx={{ marginLeft: '5px' }} size={14} />
                )}
              </Typography>
              <Typography component={SecondaryText} variant="body2">
                {snapshotLength} weeks, End date: {snapshotEndDate}
              </Typography>
            </DetailSnapshot>

            {userEmail === projectOwnerEmail && (
              <PublicPrivateIconContainer>
                {isProjectAccessUpdating && projectCardAccessUpdating ? (
                  <CircularProgress size={20} />
                ) : (
                  <>
                    {projectAccessType?.projectAccess?.visibility ===
                      PROJECT_VISIBLITY.PUBLIC && (
                      <LockOpenOutlined
                        component={DeleteIconStyle}
                        color="primary"
                        onClick={() => {
                          setProjectCardAccessUpdating(true)
                          const state = { ...projectAccessType }
                          state.projectAccess.visibility = 'private'
                          for (const key in state.scenarioAccess) {
                            state.scenarioAccess[key] = 'private'
                          }
                          handlePlanAccessChangeClick(projectId, state)
                        }}
                      />
                    )}
                    {projectAccessType?.projectAccess?.visibility ===
                      PROJECT_VISIBLITY.PRIVATE && (
                      <LockOutlined
                        component={DeleteIconStyle}
                        color="primary"
                        onClick={() => {
                          setProjectCardAccessUpdating(true)
                          const access = {
                            projectAccess: {
                              visibility: 'public' as 'public' | 'private',
                            },
                            scenarioAccess: {
                              ...projectAccessType.scenarioAccess,
                            },
                          }
                          handlePlanAccessChangeClick(projectId, access)
                        }}
                      />
                    )}
                  </>
                )}
              </PublicPrivateIconContainer>
            )}
          </Detail>
        )}

        {!!workcentres && (
          <Detail>
            <DetailIcon>
              <WorkcentreIcon />
            </DetailIcon>
            <Typography variant="body1">
              <Bold>{workcentres}</Bold>{' '}
              {workcentres > 1 ? 'Work centres' : 'Work centre'}
            </Typography>
          </Detail>
        )}

        {scenarios && scenarios > 0 && (
          <Detail>
            <DetailIcon>
              <ScenarioIcon />
            </DetailIcon>
            <Typography variant="body1">
              <Bold>{scenarios}</Bold>{' '}
              {scenarios > 1 ? 'Scenarios' : 'Scenario'}
            </Typography>
          </Detail>
        )}
      </CardContent>
      <GoToIcon>
        <Button
          disabled={isDeleting}
          onClick={() => snapshotStatus === COMPLETED && handleClick(projectId)}
          sx={{
            '&.MuiButton-text:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          <ViewFont>View</ViewFont>
          <ChevronRight />
        </Button>
      </GoToIcon>
      <CardActions component={Footer}>
        <FooterDetail>
          <FooterDetailIcon>
            <UserIcon />
          </FooterDetailIcon>
          <Typography component={SecondaryText} variant="body1">
            {createdBy}
          </Typography>
        </FooterDetail>
        <Timestamp>
          <FooterDetail mb={0.5}>
            <FooterDetailIcon>
              <DateIcon />
            </FooterDetailIcon>
            <Typography component={FooterText} variant="body2">
              {format(new Date(createdAt), 'dd/MM/yyyy')}
            </Typography>
          </FooterDetail>
          <FooterDetail>
            <FooterDetailIcon>
              <TimeIcon />
            </FooterDetailIcon>
            <Typography component={FooterText} variant="body2">
              {format(new Date(createdAt), 'HH:mm')}
            </Typography>
          </FooterDetail>
        </Timestamp>
      </CardActions>
    </Wrapper>
  )
}
